import { memo } from 'react';

import { Button } from '@picsart/design-system/components/Button';
import { IconGenAI } from '@picsart/design-system/foundation/icons/IconGenAI';
import { IconCoins } from '@picsart/design-system/foundation/icons/IconCoins';
import { IconBookmarkFilled } from '@picsart/design-system/foundation/icons/IconBookmarkFilled';
import { IconBookmarkOutline } from '@picsart/design-system/foundation/icons/IconBookmarkOutline';
import { IconEditOutline } from '@picsart/design-system/foundation/icons/IconEditOutline';
import { IconShareOutline } from '@picsart/design-system/foundation/icons/IconShareOutline';
import { useTranslator } from '@picsart/rc/services/localization';
import errorLogger, {
  BUGSNAG_STATUSES,
} from '@picsart/growth-rc/services/errorLogger';

import useSavedTemplates from '@/hooks/useSavedTemplates';
import PresetLikeButton from '@/components/PresetLikeButton';
import { usePresetShare } from '@/features/preset-sharing';
import { isPortfolioApp as checkIsPortfolioApp } from '@/utils/environment/surface';
import { ScreenType } from '@/types/analytics';
import { useProjectCredits } from '@/features/credits';
import Spinner from '@/components/Spinner';
import useStyles from './styles';

import type { FeedActionBarProps } from './types';

const FeedActionBar = ({
  label,
  shareTarget,
  onUpload,
  isLoading,
  documentId,
  slug,
  flowId,
  shouldLoadCredits,
  canEdit,
  onEdit,
}: FeedActionBarProps) => {
  const classes = useStyles();
  const translate = useTranslator();
  const isPortfolioApp = checkIsPortfolioApp();
  const { isSaved, toggleSaved } = useSavedTemplates();
  const { sharePreset, sharingSlug } = usePresetShare({
    screenType: ScreenType.PRESET_FEED,
  });
  // Quoted by the calculator API rather than read off the CMS preset.
  const { credits, isLoading: areCreditsLoading } = useProjectCredits({
    flowId,
    enabled: shouldLoadCredits,
  });
  const saved = isSaved(slug);
  const SaveIcon = saved ? IconBookmarkFilled : IconBookmarkOutline;
  const isSharing = sharingSlug === slug;

  const handleShare = () => {
    sharePreset(shareTarget).catch((error) =>
      errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN)
    );
  };

  return (
    <div className={classes.actionBar}>
      {/* One rail for every secondary action, so Edit / Save / Share stack
          without any of them needing an offset that encodes the others'
          heights. Save is the only portfolio-gated one; the rest ship on all
          touchpoints. No Copy link here — Share reaches the OS sheet, which
          carries its own copy entry. */}
      <div className={classes.sideRail}>
        {canEdit && (
          <button
            type="button"
            className={classes.railButton}
            onClick={onEdit}
            aria-label="Edit inputs"
            data-testid="feed-edit"
          >
            <IconEditOutline className={classes.railIcon} fill="currentColor" />
            <span className={classes.railLabel}>{translate('Edit')}</span>
          </button>
        )}

        <PresetLikeButton
          documentId={documentId}
          slug={slug}
          screenType={ScreenType.PRESET_FEED}
        />

        {isPortfolioApp && (
          <button
            type="button"
            className={classes.railButton}
            onClick={() => toggleSaved(slug, ScreenType.PRESET_FEED)}
            aria-label={saved ? 'Remove from saved' : 'Save'}
            data-testid="feed-save"
          >
            <SaveIcon className={classes.railIcon} fill="currentColor" />
            <span className={classes.railLabel}>{translate('Save')}</span>
          </button>
        )}

        {/* Ships on every touchpoint — the link's target app is resolved from
            the surface inside the hook, not branched on here. */}
        <button
          type="button"
          className={classes.railButton}
          onClick={handleShare}
          disabled={isSharing}
          aria-label="Share"
          data-testid="feed-share"
        >
          {isSharing ? (
            <Spinner size="small" className={classes.railSpinner} />
          ) : (
            <IconShareOutline
              className={classes.railIcon}
              fill="currentColor"
            />
          )}
          <span className={classes.railLabel}>{translate('Share')}</span>
        </button>
      </div>

      <Button
        dataTestId="preset-feed-upload-button"
        className={classes.uploadButton}
        variant="filled"
        size="large"
        isFullWidth
        startIcon={IconGenAI}
        onClick={onUpload}
        isLoading={isLoading}
        isDisabled={isLoading || areCreditsLoading}
      >
        {label}
      </Button>

      {areCreditsLoading && (
        <div className={classes.creditsChip}>
          <Spinner size="xsmall" className={classes.creditsSpinner} />
        </div>
      )}

      {!areCreditsLoading && !!credits && credits > 0 && (
        <div className={classes.creditsChip}>
          <IconCoins className={classes.creditsIcon} fill="currentColor" />
          <span>{credits}</span>
        </div>
      )}
    </div>
  );
};

export default memo(FeedActionBar);
